/**
 * @voltxampere/components — SPDT-style controlled switch (Sprint 22 B1)
 *
 * A two-terminal element whose conductance is selected at solve
 * time by a boolean `closed` flag plus optional Ron / Roff
 * resistances.  The DC + transient + AC stamps degenerate to a
 * resistor of value Ron (closed) or Roff (open) — there is no
 * state, no dynamics, no nonlinearity. Animations of switch
 * toggling are a UI concern; the engine treats each solve as a
 * snapshot.
 *
 * Param defaults (Sprint 22 plan):
 *
 *   closed: false       → switch starts in the OPEN position
 *   Ron:    1 mΩ        → near-ideal closed conductance (G ≈ 1000 S)
 *   Roff:   1 GΩ        → near-ideal open conductance  (G ≈ 1e-9 S)
 *
 * The Roff default matches Gmin so a stand-alone open switch is
 * naturally well-conditioned by the existing Gmin-stepping fallback.
 *
 * Pin layout: two unlabelled terminals on the schematic body's left
 * and right edges (SPDT-shaped UI artwork lives in
 * `@voltxampere/ui` `switch.ts` symbol). Identifiers `t1` / `t2`
 * mirror the resistor convention so the SPICE codec can reuse the
 * standard two-terminal element template.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const SwitchParamsSchema = z
    .object({
    /** Whether the switch is in the closed (conducting) position. */
    closed: z.boolean().default(false),
    /** On-resistance when closed (Ω). Default 1 mΩ — near-ideal. */
    Ron: z.number().positive().finite().default(1e-3),
    /** Off-resistance when open (Ω). Default 1 GΩ — Gmin-aligned. */
    Roff: z.number().positive().finite().default(1e9),
})
    .strict();
export const switchDefinition = {
    type: "switch",
    category: "passive",
    displayName: { tr: "Anahtar", en: "Switch" },
    symbol: "SW",
    pins: [
        { id: "t1", side: "left", offset: 0, label: "1" },
        { id: "t2", side: "right", offset: 0, label: "2" },
    ],
    parameterSchema: SwitchParamsSchema,
    nonlinear: false,
};
//# sourceMappingURL=switch.js.map